import {
  Row,
  Col,
  Card,
  Empty,
  Button,
  Space,
  Typography,
  Select,
  App,
} from "antd";
import {
  memo,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Loader from "../loader";
import { CSVLink } from "react-csv";
import { map, uniqueId } from "lodash";
import Search from "antd/es/input/Search";
import Fuse from "fuse.js";
import VirtualTable from "./VirtualTable";
import { useDebounce } from "../../hooks/useDebounce";
import { useAccess } from "../../provider/AccessProvider";

const SearchInput = ({ onChange = () => {}, value }) => {
  return (
    <Search
      placeholder="Type to search..."
      id="antd-search-input"
      style={{
        width: 200,
      }}
      onChange={onChange}
      value={value}
      maxLength={20}
    />
  );
};
const safe = (v) => {
  if (v === null || v === "" || v === undefined) return "-";
  let val = String(v);
  val = val.replace(/"/g, "'");
  return val;
};
const ExportCsv = ({
  data = [],
  exportValue = [],
  exportHead = [],
}) => {
  const { message } = App.useApp();
  const csvData = useMemo(() => {
    if (data.length === 0) return [];

    if (exportHead.length > 0) {
      const th = exportHead;
      const filteredTd = data.map((row) =>
        exportValue.map((key) => {
          if (typeof key === "object") {
            return safe(key.render(row));
          }
          return safe(row[key]);
        })
      );

      return [th, ...filteredTd];
    }

    // default mode
    const th = Object.keys(data[0]).map((i) => i.toUpperCase());
    const td = data.map((i) => Object.values(i));
    return [th, ...td];
  }, [data, exportHead, exportValue]);

  const uniqueCsvId = useRef(uniqueId("csv-download-"));

  return (
    <>
      <CSVLink
        id={uniqueCsvId.current}
        style={{
          height: 0,
          width: 0,
        }}
        filename={"reports.csv"}
        data={csvData}
      />

      <Button
        onClick={() => {
          if (csvData.length === 0) {
            message.error("No data to export");
            return;
          }
          document.getElementById(uniqueCsvId.current).click();
        }}
        type="primary"
        ghost
      >
        CSV Export
      </Button>
    </>
  );
};

function CustomTable({
  title,
  columns = [],
  data = [],
  extraAction,
  loading = false,
  excelExport = false,
  exportHead = [],
  exportValue = [],
  style = {},
  containerStyle,
  emptyContainerStyle = {},
  csvAccessKey = "",
  ...restProps
}) {
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const deferredQuery = useDeferredValue(searchValue);
  const debounceSearch = useDebounce(deferredQuery, 500);
  const tableRef = useRef();

  const filterSearchingData = useMemo(() => {
    if (debounceSearch) {
      let searchKeys = [];
      if (data.length > 0) {
        searchKeys = Object.keys(data[0]);
      }
      const fuseSearch = new Fuse(data, {
        shouldSort: true,
        threshold: 0.1,
        location: 0,
        distance: 100,
        findAllMatches: false,
        minMatchCharLength: 1,
        keys: searchKeys,
        includeMatches: false,
      });
      const result = fuseSearch.search(debounceSearch).map((i) => i.item);
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;
      return {
        filterData: result.slice(start, end),
        data: result,
      };
    } else if (pageSize && pageNumber) {
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;
      const result = data.slice(start, end);
      return {
        filterData: result,
        data,
      };
    }
  }, [debounceSearch, data, pageSize, pageNumber]);

  const Count = ({ style }) => (
    <Typography.Text style={style} type="secondary">
      {data.length > 0 ? `Count: ${filterSearchingData.data.length}` : ""}
    </Typography.Text>
  );

  const handlePagination = (pageNumberValue) => {
    setPageNumber(pageNumberValue);
  };

  return (
    <div style={{ ...containerStyle, position: "relative" }} className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace"
            style={{
              marginBottom: 4,
            }}
            title={title}
            extra={
              <Space size={8} direction="horizontal">
                <SearchInput
                  value={searchValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchValue(value);
                    setPageNumber(1);
                  }}
                />
                {excelExport && (
                  <ExportCsv
                    data={filterSearchingData.data}
                    exportHead={exportHead}
                    exportValue={exportValue}
                    csvAccessKey={csvAccessKey}
                  />
                )}
                {extraAction}
              </Space>
            }
          >
            <div className="table-responsive">
              {filterSearchingData.filterData?.length === 0 ? (
                <div style={{ position: "relative" }}>
                  {loading && (
                    <span
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <Loader size={60} />
                    </span>
                  )}
                  <Empty
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "min(100vh - 400px, 690px)",
                      ...emptyContainerStyle,
                    }}
                  />
                </div>
              ) : (
                <>
                  <VirtualTable
                    ref={tableRef}
                    columns={columns}
                    dataSource={filterSearchingData.data}
                    loading={{
                      spinning: loading,
                      indicator: <Loader size={45} />,
                    }}
                    style={{
                      minHeight: 380,
                      ...style,
                    }}
                    className="ant-border-space"
                    {...restProps}
                    pagination={
                      restProps?.pagination === false
                        ? false
                        : {
                            pageSizeOptions: ["5", "10", "20", "50", "100 "],
                            selectComponentClass: Select,
                            total: filterSearchingData.data.length,
                            defaultPageSize: 5,
                            showTotal: (total) => `Total ${total} items`,
                          }
                    }
                  />
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default memo(CustomTable);
